import type { FC } from 'react';
import { Typography, Stack, AccordionDetails, Button } from '@mui/material';
import { VyIcon, VyIconType } from '@vy/ui-design-system';
import { isInvoiceSyncToIntegrationAllowed } from '@vy/invoices';
import { INVOICE_REVIEW_STATUSES } from '@vy/types';
import { isNotEmptyString, raiseUnreachable, withDefault, now } from '@vy/utils';
import type { Optional, ItemOf } from '@vy/utils';
import { formatDate } from '../../../../../../../../../../../../utils/date';
import { runMutation } from '../../../../../../../../../../../../utils/vy-mutation';
import { getXeroInvoiceUrl } from '../../../../../../../../../../../../services/integration-invoice/xero-integration-invoice-link';
import { useQueryClient } from 'react-query';
import type { GetLightInvoicesResponse, GetExportedIntegrationsResponse } from '@vy/api-definition';
import { SYNC_JOB_STATUSES } from '@vy/integrations-types';
import type { SyncJobStatus } from '@vy/integrations-types';
import { useXeroExportInvoices } from '../../../../../../../../../../../../hooks/requests/integrations';
import { queryKeys } from '../../../../../../../../../../../../services/query-key/query-keys';

type XeroIntegrationStatusAccordionDetailsProps = {
  xeroIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const XeroIntegrationStatusAccordionDetails: FC<XeroIntegrationStatusAccordionDetailsProps> = ({
  xeroIntegration,
  invoice,
}) => {
  const exportToXeroMutation = useXeroExportInvoices();
  const queryClient = useQueryClient();

  const isApproved = invoice.reviewStatus === INVOICE_REVIEW_STATUSES.Approved;
  const isSyncable = isInvoiceSyncToIntegrationAllowed(invoice);
  const isSynced = xeroIntegration?.status === SYNC_JOB_STATUSES.COMPLETED;
  const status = withDefault(xeroIntegration?.status, SYNC_JOB_STATUSES.PENDING);
  const label = getLabel(status);
  const icon = getIcon(status);

  const getStatusText = () => {
    if (isSynced) {
      if (xeroIntegration?.updatedAt) {
        return formatDate(xeroIntegration.updatedAt);
      }
      return formatDate(now());
    }
    if (!isApproved) {
      return 'Approve invoice to enable syncing';
    }
    if (!isSyncable) {
      return 'Child invoices sync through the parent customer';
    }
    if (xeroIntegration?.updatedAt) {
      return formatDate(xeroIntegration.updatedAt);
    }
    return formatDate(now());
  };

  const handleSyncToXero = async () => {
    await runMutation(
      exportToXeroMutation,
      { invoiceIds: [invoice.id] },
      {
        pending: 'Requesting sync to Xero...',
        success: 'Sync invoice initialized',
        error: 'Failed to initialize sync to Xero',
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries(queryKeys.invoice(invoice.id)),
            queryClient.invalidateQueries(queryKeys.exportedInvoiceIntegrations(invoice.id)),
          ]);
        },
      },
    );
  };

  const handleOpenOnXero = () => {
    const externalId = xeroIntegration?.integrationExternalId;

    if (isNotEmptyString(externalId)) {
      const url = getXeroInvoiceUrl(externalId);

      window.open(url, '_blank');
    }
  };

  return (
    <AccordionDetails sx={{ p: 0 }}>
      <Stack
        sx={{
          gap: 2,
          mb: 1.5,
          mt: 1.5
        }}>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: "74px",
            flexShrink: 0,
            justifyContent: "space-between"
          }}>
          <Stack
            direction="column"
            sx={{
              alignItems: "flex-start",
              gap: "4px"
            }}>
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                gap: 1
              }}>
              <VyIcon icon={icon} strokeOpacity={0} />
              <Typography
                sx={{
                  fontSize: 14,
                  lineHeight: "20px"
                }}>
                {label}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                lineHeight: "20px",
                color: "#7E8796"
              }}>
              {getStatusText()}
            </Typography>
          </Stack>

          {!isSynced && (
            <Button
              variant="text"
              size="medium"
              startIcon={<VyIcon icon="Synchronize" transform="scale(0.7)" />}
              onClick={handleSyncToXero}
              disabled={!isSyncable}
            >
              Sync
            </Button>
          )}
        </Stack>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          endIcon={<VyIcon icon="OpenNew" />}
          onClick={handleOpenOnXero}
          disabled={!isSynced}
        >
          Open on Xero
        </Button>
      </Stack>
    </AccordionDetails>
  );
};

function getLabel(status: SyncJobStatus): string {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'Xero not synced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'Sync failed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'Synced to Xero';
    default:
      return raiseUnreachable(status);
  }
}

function getIcon(status: SyncJobStatus): VyIconType {
  switch (status) {
    case SYNC_JOB_STATUSES.IN_PROGRESS:
    case SYNC_JOB_STATUSES.PENDING:
      return 'XeroNotSynced';
    case SYNC_JOB_STATUSES.FAILED:
      return 'XeroSyncFailed';
    case SYNC_JOB_STATUSES.COMPLETED:
      return 'XeroSynced';
    default:
      return raiseUnreachable(status);
  }
}
